import { PublicNav, PublicFooter } from "@/components/app/PublicNav";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, BookOpen, Building2, Calculator, Factory, FileText, GraduationCap, Leaf, ShieldCheck, Sparkles } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxzdXN0YWluYWJsZSUyMGNvbmNyZXRlJTIwY29uc3RydWN0aW9uJTIwbW9kZXJufGVufDB8fHx8MTc4NTc0OTQ2Nnww&ixlib=rb-4.1.0&q=85";
const INNOVATION = "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb25zdHJ1Y3Rpb24lMjBlbmdpbmVlcnMlMjBzaXRlfGVufDB8fHx8MTc4NTc0OTQ3Mnww&ixlib=rb-4.1.0&q=85";

const stats = [
  { k: "Full Costing", v: "Bahan • TK • Overhead • Lingkungan" },
  { k: "Green Accounting", v: "Identifikasi biaya lingkungan sistematis" },
  { k: "Multi-Tenant", v: "Aman untuk beberapa perusahaan sekaligus" },
  { k: "Laporan Otomatis", v: "Ekspor PDF & Excel siap tanda tangan" },
];

const features = [
  { icon: Calculator, title: "HPP Full Costing", desc: "Perhitungan otomatis bahan baku, tenaga kerja, overhead, hingga biaya lingkungan yang dibebankan." },
  { icon: Leaf, title: "Green Accounting", desc: "Identifikasi dan alokasi biaya lingkungan secara sistematis dengan pencegahan double counting." },
  { icon: Factory, title: "Mix Design Fleksibel", desc: "Rekam formula dan substitusi material, kelola versi resep yang telah diuji laboratorium." },
  { icon: BarChart3, title: "Perbandingan Standar vs Aktual", desc: "Analisis varians harga dan kuantitas, sorot penyebab selisih secara transparan." },
  { icon: ShieldCheck, title: "Multi-Tenant & RBAC", desc: "Data antar perusahaan pengguna terisolasi. Tujuh peran dengan hak akses yang dijaga di sisi server." },
  { icon: FileText, title: "Laporan PDF & Excel", desc: "Cetak Executive Summary, rincian HPP, laporan varians, dan simulasi harga jual dalam sekejap." },
];

const flow = [
  { n: "01", t: "Master Data", d: "Input produk, bahan, supplier, tenaga kerja, mesin, kategori biaya." },
  { n: "02", t: "Mix Design", d: "Susun formula per produk beserta faktor waste dan substitusi material." },
  { n: "03", t: "Batch Produksi", d: "Rekam realisasi bahan, tenaga kerja, mesin, dan biaya lingkungan." },
  { n: "04", t: "Perhitungan HPP", d: "Sistem menghasilkan HPP standar & aktual, lengkap dengan varians." },
  { n: "05", t: "Harga Jual", d: "Simulasikan metode markup atau margin, banding harga pasar." },
  { n: "06", t: "Laporan", d: "Ekspor PDF/Excel untuk pimpinan, mitra, dan tim pengabdian." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50">
      <PublicNav />

      {/* Hero */}
      <section className="relative overflow-hidden isolate">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-700 via-emerald-800 to-[#052E1C]" />
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-emerald-400/25 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-teal-400/20 blur-3xl" />
          <div className="gc-grain absolute inset-0" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-white backdrop-blur-sm" data-testid="hero-badge">
              <Sparkles className="w-3.5 h-3.5" /> Produk Inovasi • Politeknik Negeri Manado
            </span>
            <h1 className="mt-6 font-cabinet text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05]" data-testid="hero-title">
              GREENCON HPP
            </h1>
            <p className="mt-5 font-cabinet text-xl sm:text-2xl text-white font-semibold leading-snug max-w-2xl">
              Sistem Informasi Akuntansi Harga Pokok Produksi Beton Rendah Karbon
            </p>
            <p className="mt-5 text-white/90 text-base sm:text-lg max-w-2xl leading-relaxed">
              Platform digital untuk membantu perhitungan Harga Pokok Produksi menggunakan metode <em className="text-white">Full Costing</em> yang terintegrasi dengan komponen biaya lingkungan dan mendukung pengelolaan biaya produksi yang lebih <span className="font-semibold text-white underline decoration-white/40 underline-offset-4">akurat, transparan, dan berkelanjutan</span>.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
              <GraduationCap className="w-3.5 h-3.5 text-white" /> Produk Inovasi Politeknik Negeri Manado
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/login" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-emerald-800 font-semibold shadow-lg hover:bg-emerald-50 hover:scale-[1.02] active:scale-95 transition-transform" data-testid="hero-login">Masuk ke Sistem <ArrowRight className="w-4 h-4" /></Link>
              <a href="#tentang" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-white/60 bg-transparent text-white text-sm font-semibold hover:bg-white/10 backdrop-blur-sm transition-colors" data-testid="hero-learn">
                <BookOpen className="w-4 h-4" /> Pelajari Sistem
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-white/10 backdrop-blur-xl border border-white/20 p-6 shadow-2xl">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/80">Kapabilitas Sistem</p>
              <div className="mt-4 space-y-3">
                {stats.map((s) => (
                  <div key={s.k} className="rounded-2xl bg-white/10 border border-white/20 p-4">
                    <div className="font-cabinet text-lg font-black text-white">{s.k}</div>
                    <div className="mt-1 text-xs text-white/80 leading-snug">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tentang GREENCON HPP */}
      <section id="tentang" className="max-w-6xl mx-auto px-6 lg:px-10 py-20 scroll-mt-24">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="gc-label">Tentang GREENCON HPP</span>
            <h2 className="gc-h1 mt-3">Inovasi akademik untuk digitalisasi HPP beton rendah karbon.</h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-slate-600 leading-relaxed">
            <p>GREENCON HPP merupakan <strong>sistem informasi akuntansi berbasis web</strong> yang dikembangkan dari hasil penelitian <strong>Politeknik Negeri Manado</strong> untuk mendukung digitalisasi perhitungan Harga Pokok Produksi pada produk beton rendah karbon.</p>
            <p>Sistem ini dirancang untuk dapat <strong>diterapkan dan didiseminasikan kepada industri konstruksi</strong> melalui kegiatan hilirisasi dan pengabdian kepada masyarakat.</p>
            <div className="grid sm:grid-cols-3 gap-4 pt-4">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
                <BookOpen className="w-5 h-5 text-emerald-600" />
                <div className="mt-2 font-cabinet font-bold text-slate-900">Hasil Penelitian</div>
                <p className="text-xs text-slate-600 mt-1">Dikembangkan dari kajian akademik SIA di lingkungan Politeknik Negeri Manado.</p>
              </div>
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
                <Sparkles className="w-5 h-5 text-emerald-600" />
                <div className="mt-2 font-cabinet font-bold text-slate-900">Hilirisasi</div>
                <p className="text-xs text-slate-600 mt-1">Diseminasikan ke industri konstruksi melalui pengabdian kepada masyarakat.</p>
              </div>
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
                <Leaf className="w-5 h-5 text-emerald-600" />
                <div className="mt-2 font-cabinet font-bold text-slate-900">Berkelanjutan</div>
                <p className="text-xs text-slate-600 mt-1">Terintegrasi dengan konsep green accounting untuk konstruksi ramah lingkungan.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-2xl">
            <span className="gc-label">Fitur Unggulan</span>
            <h2 className="gc-h1 mt-3">Semua yang dibutuhkan untuk menghitung HPP beton rendah karbon.</h2>
            <p className="mt-4 text-slate-600">Dari mix design hingga laporan pimpinan, GREENCON HPP membantu tim akademik Politeknik Negeri Manado dan industri konstruksi mengelola biaya produksi secara profesional.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="gc-card group hover:-translate-y-1 transition-transform duration-300" data-testid={`feature-${f.title}`}>
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="gc-h3 mt-4">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <span className="gc-label">Alur Kerja</span>
            <h2 className="gc-h1 mt-3">Enam langkah, satu HPP yang dapat dipertanggungjawabkan.</h2>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
            {flow.map((s) => (
              <div key={s.n} className="rounded-2xl border border-slate-200 p-5 hover:border-emerald-400 transition-colors bg-white">
                <div className="flex items-start gap-3">
                  <div className="font-cabinet text-3xl font-black text-emerald-500 leading-none">{s.n}</div>
                  <div>
                    <div className="font-cabinet font-bold text-slate-900">{s.t}</div>
                    <p className="text-sm text-slate-600 mt-1">{s.d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hilirisasi / Pengguna Sasaran */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <span className="gc-label">Hilirisasi & Pengabdian Masyarakat</span>
            <h2 className="gc-h1 mt-3">Dari laboratorium kampus ke industri konstruksi.</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">Sebagai bagian dari kegiatan pengabdian kepada masyarakat, sistem ini dapat diterapkan pada berbagai perusahaan produsen beton. Setiap perusahaan pengguna memiliki ruang datanya sendiri dan bertanggung jawab atas data operasionalnya masing-masing, sedangkan sistem tetap dikelola dan dikembangkan oleh Politeknik Negeri Manado.</p>
            <div className="mt-6 flex flex-wrap gap-4">
              <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 px-4 py-2.5">
                <GraduationCap className="w-5 h-5 text-emerald-600" />
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500">Pengembang Sistem</div>
                  <div className="text-sm font-semibold text-slate-900">Politeknik Negeri Manado</div>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2.5">
                <Building2 className="w-5 h-5 text-slate-600" />
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500">Sasaran Diseminasi</div>
                  <div className="text-sm font-semibold text-slate-900">Industri konstruksi & produsen beton</div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-200">
              <img src={INNOVATION} alt="Kegiatan hilirisasi" className="w-full h-[380px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <div className="text-xs uppercase tracking-widest text-emerald-300">Hilirisasi</div>
                <div className="font-cabinet text-2xl font-bold mt-1">Dari laboratorium ke lapangan.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="relative overflow-hidden rounded-3xl bg-[#0B192C] p-10 lg:p-14">
          <div className="gc-grain absolute inset-0" />
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-cabinet text-3xl font-black text-white">Siap menghitung HPP dengan lebih akurat?</h3>
              <p className="mt-3 text-slate-300 max-w-md">Masuk ke sistem untuk merasakan perhitungan HPP end-to-end dari mix design hingga simulasi harga jual.</p>
            </div>
            <div className="flex md:justify-end gap-2 flex-wrap">
              <Link to="/login" className="gc-btn-primary" data-testid="cta-login">Masuk ke Sistem <ArrowRight className="w-4 h-4" /></Link>
              <a href="#tentang" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 text-white text-sm font-semibold hover:bg-white/10 transition-colors" data-testid="cta-learn"><BookOpen className="w-4 h-4" /> Pelajari Sistem</a>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
